import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../lib/config';
import { getApiErrorMessage } from '../../lib/apiError';
import { optionalGeolocationHeader } from '../../lib/geolocation';
import { clearLinkFromAddressBar, readLinkToken } from '../../lib/linkToken';
import { NAVY, NAVY_LIGHT, GOLD, GOLD_LIGHT } from '../../styles/theme';

type Phase = 'working' | 'done' | 'refused' | 'invalid';

/**
 * Opens an email-verification link.
 *
 * Two things here are deliberate rather than incidental:
 *
 *  1. The token is stripped from the address bar before anything else happens,
 *     so it cannot leak through a Referer header, the browser history or a
 *     screenshot, and it is submitted in a POST body rather than a query string.
 *  2. Location is offered, never demanded. The backend treats its absence as one
 *     risk signal among several; blocking here would mean a user who declines
 *     the browser prompt could never confirm their address.
 */
export default function VerifyEmail(): JSX.Element {
  const [phase, setPhase] = useState<Phase>('working');
  const [message, setMessage] = useState('');
  // The effect must run once even under StrictMode's double-invoke, because the
  // token is single-use: a second POST would consume nothing and report failure.
  const started = useRef(false);

  const submit = useCallback(async (token: string) => {
    // Declined or unavailable is fine here: the backend scores the verification
    // with the attestation when it arrives and without it when it does not.
    const headers = await optionalGeolocationHeader();
    try {
      await axios.post(`${API_URL}/api/v1/auth/email/verification`, { token }, { headers });
      setPhase('done');
    } catch (err: unknown) {
      const refused = axios.isAxiosError(err) && err.response?.status === 409;
      setPhase(refused ? 'refused' : 'invalid');
      setMessage(getApiErrorMessage(err, ''));
    }
  }, []);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const token = readLinkToken();
    // Strip before the first network call, not after.
    clearLinkFromAddressBar();

    if (!token) {
      setPhase('invalid');
      setMessage('This link is incomplete. Please request a new one.');
      return;
    }
    void submit(token);
  }, [submit]);

  const heading = {
    working: 'Confirming your address…',
    done: 'Your email is confirmed',
    refused: 'We could not confirm this link',
    invalid: 'This link is no longer valid',
  }[phase];

  const body = {
    working: 'One moment.',
    done: 'Thank you. We can now use this address to reach you about your account.',
    refused:
      message ||
      'It looks like this link was opened somewhere unexpected. Please contact support so we can confirm your address with you directly.',
    invalid: message || 'Links can be used once and expire quickly. Please request a new one.',
  }[phase];

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{ background: `linear-gradient(135deg, ${NAVY}, ${NAVY_LIGHT})` }}
    >
      <div
        className="w-full max-w-md rounded-2xl p-8"
        style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
      >
        <h1 className="text-xl font-semibold mb-3" style={{ color: '#FFFFFF' }}>
          {heading}
        </h1>
        <p className="text-sm leading-relaxed mb-6" style={{ color: 'rgba(255,255,255,0.7)' }}>
          {body}
        </p>
        {phase !== 'working' && (
          <Link
            to="/login"
            className="inline-block rounded-xl px-6 py-3 text-sm font-semibold no-underline"
            style={{
              background: `linear-gradient(135deg, ${GOLD}, ${GOLD_LIGHT})`,
              color: NAVY,
            }}
          >
            Continue to sign in
          </Link>
        )}
      </div>
    </div>
  );
}
